import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";

import { AjsResponse, wrapAjs } from "../ajs-response";

/**
 * TenantApiService (Angular 18).
 *
 * Replaces apps/b2b-ui/common/services/api/tenantApi/tenantApi.service.js.
 *
 * Top-level CRUD on `/v1/api/tenant`, plus three sub-namespaces:
 *   apiService.tenant.reasons.list      → refund reasons
 *   apiService.tenant.calendar.*        → tenant business calendar
 *   apiService.tenant.config.list       → tenant product config (v2)
 *
 * Bug drop: the legacy exposed `isIntegrationEnabled()` that called
 * `this.view()` (returns a Promise) and immediately read `.sf_sync` on the
 * Promise object (always `undefined`), additionally writing `data` to the
 * global scope. No consumers reference it (verified by grep), so the method
 * is omitted here.
 */
const TENANT_BASE_V1 = "/v1/api/tenant";
const TENANT_CALENDAR_BASE_V1 = `${TENANT_BASE_V1}/calendar`;
const TENANT_REFUND_REASONS_BASE_V1 = `${TENANT_BASE_V1}/settings/refund/reasons`;
const TENANT_PRODUCT_CONFIG_BASE_V2 = "/v2/api/tenantproductconfig";

interface TenantBody {
  id: number | string;
  firstName: string;
  lastName: string;
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  name: string;
  phoneNumber: string;
  companyUrl: string;
  timezone: string;
  logoImage: unknown;
}

function tenantBodyFields(props: TenantBody) {
  return {
    firstName: props.firstName,
    lastName: props.lastName,
    streetAddress1: props.streetAddress1,
    streetAddress2: props.streetAddress2,
    city: props.city,
    state: props.state,
    country: props.country,
    zipCode: props.zipCode,
    name: props.name,
    phoneNumber: props.phoneNumber,
    companyUrl: props.companyUrl,
    timezone: props.timezone,
    logoImage: props.logoImage,
  };
}

@Injectable({ providedIn: "root" })
export class TenantApiService {
  private readonly http = inject(HttpClient);

  view(): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(TENANT_BASE_V1));
  }

  update(props: TenantBody): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.put<unknown>(
        `${TENANT_BASE_V1}/${props.id}`,
        tenantBodyFields(props),
      ),
    );
  }

  activate(props: TenantBody): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(
        `${TENANT_BASE_V1}/${props.id}/activate`,
        tenantBodyFields(props),
      ),
    );
  }

  create(props: {
    orgName: string;
    email: string;
  }): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(`${TENANT_BASE_V1}/`, {
        orgName: props.orgName,
        email: props.email,
      }),
    );
  }

  readonly reasons = {
    // Note: legacy `reasons.list` called `$http({ url: this.service })`,
    // where `this.service` inside the `reasons` namespace was set in
    // `init()` to `/v1/api/tenant/settings/refund/reasons`. Preserved.
    list: (): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(TENANT_REFUND_REASONS_BASE_V1)),
  };

  readonly calendar = {
    detail: (): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(TENANT_CALENDAR_BASE_V1)),

    create: (props: {
      standard: unknown;
      exceptions: unknown;
    }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(TENANT_CALENDAR_BASE_V1, {
          standard: props.standard,
          exceptions: props.exceptions,
        }),
      ),

    update: (props: {
      id: number | string;
      standard: unknown;
      exceptions: unknown;
    }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.put<unknown>(TENANT_CALENDAR_BASE_V1, {
          id: props.id,
          standard: props.standard,
          exceptions: props.exceptions,
        }),
      ),
  };

  readonly config = {
    list: (): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${TENANT_PRODUCT_CONFIG_BASE_V2}/list`)),
  };
}
